export interface PPTExportParams {
  area: string;
  branch: string;
  activeEntity: string;
  activeSection: string;
  periodLabel: string;
  extractedKPIs?: { title: string; value: string; change?: string }[];
  charts?: { title: string; imgData: string }[];
  tables?: { title: string; headers: string[]; rows: string[][] }[];
}

export function exportDashboardToPPT(params: PPTExportParams): void {
  const PptxGenJS = (window as any).PptxGenJS;
  if (!PptxGenJS) {
    alert('PPT library not loaded. Check internet connection and reload.');
    return;
  }
  const prs = new PptxGenJS();
  
  // Define Master Slide with Clean Light Theme
  prs.defineSlideMaster({
    title: 'MASTER_SLIDE_LIGHT',
    background: { color: 'FCFCFD' }, // Clean Alabaster
    objects: [
      { rect: { x: 0, y: 0, w: '100%', h: 0.08, fill: { color: '8B5CF6' } } }, // Electric purple top border
      { text: { 
          text: `Generated on: ${new Date().toLocaleDateString('id-ID')} | Premium Financial Report`, 
          options: { x: 0.5, y: 5.3, w: 9, h: 0.2, fontSize: 8, color: '64748B', align: 'left' } 
      }}
    ]
  });

  // Slide 1: Premium Startup-style Cover Slide
  const s1 = prs.addSlide({ masterName: 'MASTER_SLIDE_LIGHT' });
  const entityName = params.activeEntity === 'JUARA_GADAI' ? 'Juara Gadai Group' : 'Sahabat Gadai / Juara Gadai';
  
  // Map section IDs to human readable labels
  const sectionNames: Record<string, string> = {
    'executive': 'EXECUTIVE SUMMARY',
    'dashboard': 'FINANCIAL DASHBOARD',
    'revenue': 'REVENUE ANALYSIS',
    'expenses': 'EXPENSES ANALYSIS',
    'balance': 'BALANCE SHEET STATEMENT',
    'cashflow': 'CASH FLOW STATEMENT',
    'monthly': 'MONTHLY REPORT',
    'annual': 'ANNUAL REPORT',
    'forecast': 'FORECASTING REPORT',
    'bep': 'BEP TITIK IMPAS',
    'budget': 'BUDGET VARIANCE ANALYSIS',
    'budget_plan': 'BUDGET PLAN',
    'gl_lookup': 'GENERAL LEDGER AUDIT',
    'database': 'FINANCE DATABASE',
  };
  const sectionTitle = sectionNames[params.activeSection] || params.activeSection.toUpperCase();

  s1.addText(sectionTitle, { x: 0.5, y: 1.5, w: 9, h: 0.5, fontSize: 15, bold: true, color: '8B5CF6', align: 'center', charSpacing: 3 });
  s1.addText(entityName.toUpperCase(), { x: 0.5, y: 2.0, w: 9, h: 0.8, fontSize: 38, bold: true, color: '0F172A', align: 'center' });
  s1.addText(`Area: ${params.area}${params.branch !== 'ALL' ? ` / Cabang: ${params.branch}` : ''}`, { x: 0.5, y: 2.9, w: 9, h: 0.3, fontSize: 13, color: '475569', align: 'center', bold: true });
  s1.addText(`Periode Laporan: ${params.periodLabel}`, { x: 0.5, y: 3.3, w: 9, h: 0.3, fontSize: 12, color: '64748B', align: 'center' });

  // Slide 2: KPI Ringkasan Slide (if KPIs are present)
  if (params.extractedKPIs && params.extractedKPIs.length > 0) {
    const s2 = prs.addSlide({ masterName: 'MASTER_SLIDE_LIGHT' });
    s2.addText('RINGKASAN INDIKATOR UTAMA (KPIs)', { x: 0.5, y: 0.4, w: 9, h: 0.4, fontSize: 16, bold: true, color: '0F172A' });

    // Grid details for up to 6 KPIs (2 rows x 3 columns)
    const cardW = 2.8;
    const cardH = 1.3;
    const gapX = 0.3;
    const gapY = 0.3;
    let startX = 0.5;
    let startY = 1.2;

    params.extractedKPIs.forEach((kpi, idx) => {
      if (idx > 5) return; // Limit to top 6 KPIs in slide presentation
      
      const r = Math.floor(idx / 3);
      const c = idx % 3;
      
      const x = startX + c * (cardW + gapX);
      const y = startY + r * (cardH + gapY);

      // Add a neat card shape
      s2.addShape(prs.ShapeType.roundRect, {
        x, y, w: cardW, h: cardH,
        fill: { color: 'F8FAFC' },
        line: { color: 'E2E8F0', width: 1 }
      });

      // Add KPI Name
      s2.addText(kpi.title.toUpperCase(), {
        x: x + 0.15, y: y + 0.15, w: cardW - 0.3, h: 0.25,
        fontSize: 8.5, bold: true, color: '64748B'
      });

      // Add KPI Value
      s2.addText(kpi.value, {
        x: x + 0.15, y: y + 0.45, w: cardW - 0.3, h: 0.45,
        fontSize: 18, bold: true, color: '0F172A'
      });

      // Add KPI Change if present
      if (kpi.change) {
        const isPos = kpi.change.includes('▲') || !kpi.change.includes('-');
        s2.addText(kpi.change, {
          x: x + 0.15, y: y + 0.95, w: cardW - 0.3, h: 0.25,
          fontSize: 8.5, bold: true, color: isPos ? '10B981' : 'EF4444'
        });
      }
    });
  }

  // Slide 3+ (Charts Slide): Exactly 1 chart per slide
  const addChartSlide = (title: string, imgData: string | undefined) => {
    if (!imgData) return;
    const slide = prs.addSlide({ masterName: 'MASTER_SLIDE_LIGHT' });
    slide.addText(title, { x: 0.5, y: 0.4, w: 9, h: 0.5, fontSize: 18, bold: true, color: '0F172A' });
    slide.addImage({ data: imgData, x: 0.5, y: 1.0, w: 9.0, h: 4.0, sizing: { type: 'contain', w: 9.0, h: 4.0 } });
  };

  if (params.charts && params.charts.length > 0) {
    params.charts.forEach((chart) => {
      addChartSlide(chart.title, chart.imgData);
    });
  }

  // Slide 4+ (Tables Slide): Native PowerPoint Tables chunked cleanly
  if (params.tables && params.tables.length > 0) {
    params.tables.forEach((table) => {
      if (table.headers.length === 0 || table.rows.length === 0) return;
      
      const colCount = table.headers.length;
      const tableWidth = 9.0;
      let colWidths: number[] = [];
      if (colCount === 2) {
        colWidths = [6.0, 3.0];
      } else if (colCount === 3) {
        colWidths = [4.0, 2.5, 2.5];
      } else {
        colWidths = Array(colCount).fill(tableWidth / colCount);
      }

      // PowerPoint allows comfortable display of ~10-12 rows per slide
      const rowChunkSize = 10;
      for (let i = 0; i < table.rows.length; i += rowChunkSize) {
        const chunk = table.rows.slice(i, i + rowChunkSize);
        const slide = prs.addSlide({ masterName: 'MASTER_SLIDE_LIGHT' });
        
        const pageNumSuffix = table.rows.length > rowChunkSize ? ` (Part ${Math.floor(i / rowChunkSize) + 1})` : '';
        slide.addText(table.title + pageNumSuffix, { x: 0.5, y: 0.3, w: 9, h: 0.5, fontSize: 18, bold: true, color: '0F172A' });
        
        const pptxRows: any[][] = [];
        
        // Header Row Cells
        const headerRowCells = table.headers.map(h => ({
          text: h,
          options: { fill: 'F1F5F9', fontFace: 'Helvetica', fontSize: 10, bold: true, color: '334155', align: 'center', valign: 'middle' }
        }));
        pptxRows.push(headerRowCells);
        
        // Data Row Cells
        chunk.forEach((row, rIdx) => {
          const rowCells = row.map((cellText, cIdx) => {
            const isNumeric = cIdx > 0 && (
              cellText.startsWith('Rp') || cellText.startsWith('-Rp') || cellText.endsWith('%') || !isNaN(parseFloat(cellText.replace(/[^0-9.-]/g, '')))
            );
            
            let textColor = '1E293B';
            if (cellText.includes('✗') || cellText.includes('Ongoing') || cellText.includes('Loss') || (cellText.startsWith('-') && !cellText.includes('—'))) {
              textColor = 'EF4444';
            } else if (cellText.includes('✓') || cellText.includes('Pass') || cellText.includes('Profit') || (cellText.startsWith('+') && !cellText.includes('—'))) {
              textColor = '10B981';
            }
            
            return {
              text: cellText,
              options: {
                fill: rIdx % 2 === 1 ? 'F8FAFC' : 'FFFFFF',
                fontFace: 'Helvetica',
                fontSize: 9,
                color: textColor,
                align: isNumeric ? 'right' : 'left',
                valign: 'middle'
              }
            };
          });
          pptxRows.push(rowCells);
        });
        
        slide.addTable(pptxRows, {
          x: 0.5,
          y: 0.9,
          w: 9.0,
          colW: colWidths,
          border: { type: 'line', size: 0.5, color: 'E2E8F0' }
        });
      }
    });
  }

  const fn = `JGJB_${params.area}${params.branch !== 'ALL' ? '_'+params.branch : ''}_${params.activeSection}_Report.pptx`;
  prs.writeFile({ fileName: fn });
}
